import React from 'react';

interface ChipItem {
  label: string;
  value: string | number;
  color?: string;
  sub?: string;
}

interface Props {
  titulo: string;
  subtitulo?: string;
  icone: React.ReactNode;
  accentColor?: string;
  onVoltar?: () => void;
  chips?: ChipItem[];
}

const C = {
  white:     '#f0f4f8',
  gray:      '#8a95a3',
  grayLt:    '#bdc5cf',
  border:    'rgba(0,184,204,0.18)',
  cyan:      '#00b8cc',
};

function Decoracoes() {
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      <svg style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 320, opacity: 0.05 }}
           viewBox="0 0 320 120" preserveAspectRatio="none">
        {Array.from({ length: 8 }).map((_, i) => (
          <line key={`v${i}`} x1={i * 46} y1={0} x2={i * 46} y2={120} stroke={C.cyan} strokeWidth={0.8} />
        ))}
        {Array.from({ length: 7 }).map((_, i) => (
          <line key={`h${i}`} x1={0} y1={i * 20} x2={320} y2={i * 20} stroke={C.cyan} strokeWidth={0.8} />
        ))}
      </svg>
      <svg style={{ position: 'absolute', right: -50, top: -50, opacity: 0.08 }}
           width={240} height={240} viewBox="0 0 240 240">
        <circle cx={120} cy={120} r={100} fill="none" stroke={C.cyan} strokeWidth={1.2} />
        <circle cx={120} cy={120} r={70}  fill="none" stroke={C.cyan} strokeWidth={0.6} />
      </svg>
    </div>
  );
}

export default function ModuleHeader({
  titulo,
  subtitulo,
  icone,
  accentColor = '#0071E3',
  onVoltar,
  chips,
}: Props) {
  return (
    <>
      <style>{`
        @keyframes tovisPulseModule {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.45; transform: scale(0.82); }
        }
        .tovis-module-pulse { animation: tovisPulseModule 2s ease-in-out infinite; }
        .tovis-module-voltar:hover {
          background: rgba(0,184,204,0.10) !important;
          border-color: ${accentColor} !important;
          color: ${C.white} !important;
        }
      `}</style>

      <div
        className="no-print"
        style={{
          background: 'linear-gradient(120deg, #0a1525 0%, #132240 55%, #0d2340 100%)',
          padding: '24px 32px',
          borderBottom: `1px solid ${C.border}`,
          position: 'relative',
          overflow: 'hidden',
          marginBottom: 24,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <Decoracoes />

        <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>

          {/* Esquerda: Voltar + ícone + título + subtítulo */}
          <div>
            {/* Linha 1: Voltar + ícone + título + dot */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {onVoltar && (
                <button
                  className="tovis-module-voltar"
                  onClick={onVoltar}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 6, padding: '6px 14px', cursor: 'pointer',
                    fontSize: 13, fontWeight: 500, color: C.grayLt,
                    fontFamily: "'Inter', sans-serif",
                    transition: 'background 0.2s, border-color 0.2s, color 0.2s',
                    flexShrink: 0,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  Voltar
                </button>
              )}

              {/* Ícone do módulo */}
              <div style={{
                color: accentColor, display: 'flex', alignItems: 'center',
                fontSize: 28, flexShrink: 0,
              }}>
                {icone}
              </div>

              {/* Título */}
              <span style={{
                fontSize: 24, fontWeight: 700, color: C.white,
                letterSpacing: '-0.4px', lineHeight: 1,
              }}>
                {titulo}
              </span>

              {/* Pulsing dot */}
              <span
                className="tovis-module-pulse"
                style={{
                  display: 'inline-block', width: 8, height: 8,
                  borderRadius: '50%', flexShrink: 0,
                  background: accentColor,
                  boxShadow: `0 0 7px ${accentColor}`,
                }}
              />
            </div>

            {/* Divisória */}
            <div style={{
              width: 50, height: 2, background: accentColor,
              borderRadius: 2, marginTop: 12,
            }} />

            {/* Subtítulo */}
            {subtitulo && (
              <div style={{
                fontSize: 13, color: C.gray, marginTop: 8,
                fontWeight: 400, letterSpacing: '0.01em',
              }}>
                {subtitulo}
              </div>
            )}
          </div>

          {/* Direita: Chips */}
          {chips && chips.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {chips.map((chip, i) => (
                <div
                  key={i}
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${C.border}`,
                    borderLeft: `3px solid ${accentColor}`,
                    borderRadius: 8,
                    padding: '8px 14px',
                    display: 'flex', flexDirection: 'column', gap: 2,
                  }}
                >
                  <span style={{
                    fontSize: 11, textTransform: 'uppercase', color: C.gray,
                    letterSpacing: '0.06em', fontWeight: 500,
                  }}>
                    {chip.label}
                  </span>
                  <span style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 14, fontWeight: 600,
                    color: chip.color || C.white,
                  }}>
                    {chip.value}
                  </span>
                  {chip.sub && (
                    <span style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 12,
                      color: '#a0aec0',
                      marginTop: 3,
                      fontWeight: 500,
                    }}>
                      {chip.sub}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </>
  );
}
